import {useEffect, useState, useRef} from "react";
import * as Sentry from "@sentry/react";
import {chatApi} from "../../chatApi";
import {
    Button,
    Group,
    Stack,
    Textarea,
    Text,
    Modal,
    ThemeIcon,
    Radio,
    Title,
    Tooltip,
    ActionIcon,
} from "@mantine/core";
import {Rating} from "@mantine/core";
import {useDebouncedValue, useDisclosure, useMouse} from "@mantine/hooks";
import {bffApi} from "../../bffApi";
import {FeedbackCategory, MessageWithMetadata} from "../../../../types";
import {useTranslation} from "react-i18next";
import IconCheck from "../../../../assets/check.svg?react";
import IconStar from "../../../../assets/star.svg?react";
import IconX from "../../../../assets/x.svg?react";
import IconGripHorizontal from "../../../../assets/grip-horizontal.svg?react";
import scrollClasses from "../scrollContainer.module.css";
import classes from "./Rater.module.css";
import {useSimpleDrag} from "../../hooks/useSimpleDrag";

const NO_SCORE = 0;
const MAX_SCORE = 5;
const MIN_SCORE = 1;
export function Rater({
    message,
    threadId,
}: {
    message: MessageWithMetadata;
    threadId?: string;
}) {
    const {t} = useTranslation();
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const [rateMessage, rateMessageResult] =
        chatApi.endpoints.rateMessage.useMutation();
    const [value, setValue] = useState<number>(message.rating ?? NO_SCORE);
    const [category, setCategory] = useState<number | null>(
        message.category ?? null
    );
    const [comment, setComment] = useState<string>(message.feedback ?? "");
    const [debouncedCommentValue] = useDebouncedValue(comment, 1000);
    const isInitialMount = useRef(true);
    const lastSavedComment = useRef(message.feedback ?? "");

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (threadId && debouncedCommentValue !== lastSavedComment.current) {
            rateMessage({
                threadId,
                messagePosition: message.position,
                rating: value,
                comment: debouncedCommentValue,
                category,
            }).then((result) => {
                if (!result.error) {
                    lastSavedComment.current = debouncedCommentValue;
                } else {
                    console.error(
                        "Failed to save debounced comment:",
                        result.error
                    );
                    Sentry.captureException(result.error, {
                        tags: {feature: "chat.rate-message"},
                    });
                }
            });
        }
    }, [
        debouncedCommentValue,
        threadId,
        message.position,
        value,
        category,
        rateMessage,
    ]);
    const [
        opened,
        {open, close: closeCommentPopup, toggle: toggleCommentPopup},
    ] = useDisclosure(false);

    const handleCategoryChange = (newCategory: number) => {
        setCategory(newCategory);
        if (!threadId) {
            return;
        }
        rateMessage({
            threadId,
            messagePosition: message.position,
            rating: value,
            comment,
            category: newCategory,
        });
    };

    const handleRateMessage = (
        score: number,
        disableToggle: boolean = false
    ) => {
        if (!threadId) return;

        const oldScore = value;
        const targetScore = score;
        setValue(targetScore);
        if (!opened && targetScore !== MAX_SCORE) {
            open();
        }

        Sentry.metrics.count("chat.message_rated", 1, {
            attributes: {
                rating: String(targetScore),
                hasComment: comment.length > 0,
            },
        });

        rateMessage({
            threadId,
            messagePosition: message.position,
            rating: targetScore,
            comment,
            category,
        }).then((result) => {
            if (result.error) {
                setValue(oldScore);
            }
        });
    };

    const handleSubmit = () => {
        closeCommentPopup();
    };

    const textArea = (
        <Textarea
            value={comment}
            minRows={2}
            maxRows={10}
            resize="vertical"
            placeholder={t("rating.modal.elaborate")}
            onChange={(event) => setComment(event.target.value)}
        />
    );

    const {offset, isDragging, dragStart, resetDrag} = useSimpleDrag();

    useEffect(() => {
        if (opened) {
            resetDrag();
        }
    }, [opened]);

    return (
        <Stack pos={"relative"}>
            {value === NO_SCORE ? (
                <Tooltip label={t("rating.addRating")}>
                    <Rating
                        color="dibblue"
                        value={value}
                        onChange={(newScore) =>
                            handleRateMessage(newScore, true)
                        }
                    />
                </Tooltip>
            ) : (
                <Button
                    color={"var(--dib-brand-base)"}
                    variant="subtle"
                    size="xs"
                    onClick={open}
                    leftSection={
                        <ThemeIcon
                            pos="relative"
                            variant="transparent"
                            color={"var(--dib-brand-base)"}
                        >
                            <IconStar
                                width={16}
                                height={16}
                                style={{zIndex: 5}}
                            />
                        </ThemeIcon>
                    }
                >
                    {t("rating.editRating")}
                </Button>
            )}
            <Modal
                opened={opened}
                onClose={closeCommentPopup}
                size="lg"
                withCloseButton={false}
                mah={"50vh"}
                className={scrollClasses.scrollboxLight}
                classNames={{
                    header: classes.header,
                    overlay:
                        classes.overlay +
                        " " +
                        (isDragging ? classes.overlayDragging : ""),
                }}
                styles={{
                    inner: {
                        top: offset.y,
                        left: offset.x,
                    },
                }}
            >
                <header className={classes.header}>
                    <Group justify="space-between">
                        <ActionIcon
                            tabIndex={-1}
                            className={classes.dragHandle}
                            variant="subtle"
                            color="gneis.1"
                            onMouseDown={dragStart}
                            style={{cursor: isDragging ? "grabbing" : "grab"}}
                        >
                            <IconGripHorizontal />
                        </ActionIcon>
                        <ActionIcon
                            variant="subtle"
                            radius="xl"
                            color="gneis"
                            onClick={closeCommentPopup}
                        >
                            <IconX />
                        </ActionIcon>
                    </Group>
                </header>
                <Stack align="center" px={"xl"}>
                    <ThemeIcon
                        color={"var(--dib-brand-base)"}
                        size={"lg"}
                        radius="xl"
                    >
                        <IconCheck width={24} height={24} />
                    </ThemeIcon>
                    <Title order={3}>{t("rating.modal.title")}</Title>
                    <Text style={{textAlign: "center"}}>
                        {t("rating.modal.description")}
                    </Text>
                    <Rating
                        color="var(--dib-brand-base)"
                        value={value}
                        onChange={handleRateMessage}
                    />
                    <Radio.Group
                        w={"100%"}
                        my={"lg"}
                        value={category?.toString() ?? null}
                        onChange={(val) => handleCategoryChange(Number(val))}
                    >
                        <Stack>
                            <Radio
                                color={"var(--dib-brand-base)"}
                                size="md"
                                value={FeedbackCategory.Incorrect.toString()}
                                label={t("rating.modal.category.0")}
                            />
                            {category === FeedbackCategory.Incorrect &&
                                textArea}
                            <Radio
                                color={"var(--dib-brand-base)"}
                                size="md"
                                value={FeedbackCategory.DidNotFollowInstructions.toString()}
                                label={t("rating.modal.category.1")}
                            />
                            {category ===
                                FeedbackCategory.DidNotFollowInstructions &&
                                textArea}
                            <Radio
                                color={"var(--dib-brand-base)"}
                                size="md"
                                value={FeedbackCategory.PoorStructure.toString()}
                                label={t("rating.modal.category.2")}
                            />
                            {category === FeedbackCategory.PoorStructure &&
                                textArea}
                            <Radio
                                color={"var(--dib-brand-base)"}
                                size="md"
                                value={FeedbackCategory.PoorSources.toString()}
                                label={t("rating.modal.category.3")}
                            />
                            {category === FeedbackCategory.PoorSources &&
                                textArea}
                            <Radio
                                color={"var(--dib-brand-base)"}
                                size="md"
                                value={FeedbackCategory.Outdated.toString()}
                                label={t("rating.modal.category.5")}
                            />
                            {category === FeedbackCategory.Outdated && textArea}
                            <Radio
                                color={"var(--dib-brand-base)"}
                                size="md"
                                value={FeedbackCategory.Other.toString()}
                                label={t("rating.modal.category.4")}
                            />
                            {category === FeedbackCategory.Other && textArea}
                        </Stack>
                    </Radio.Group>
                    <Group w={"100%"} my={"lg"}>
                        <Button
                            color={"var(--dib-brand-base)"}
                            size="sm"
                            onClick={handleSubmit}
                        >
                            {t("rating.modal.button")}
                        </Button>
                        <Button
                            variant="subtle"
                            size="sm"
                            color={"gneis"}
                            onClick={closeCommentPopup}
                        >
                            {t("rating.modal.cancel")}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
}
